import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from '@heroui/react';
import { Icon } from '@iconify/react'; // For footer telegram icon
import { Typography, Select, AlertBlock, ResponsiveModal } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { useAuth } from '../../../../contexts/AuthContext';
import { RevshareOffer } from '../types';
import partnerRevshareService from '../../../../api/partner/partnerRevshareService';
import { showCreatorSuccess, showError } from '../../../../utils/toast';
import { getProposalSources } from '../../../../client/subscribers/Proposals/constants';
import { GreenGaleryIc } from '../../../../components/icons';

interface StartCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: RevshareOffer;
  onSuccess?: () => void;
}

const StartCampaignModal: React.FC<StartCampaignModalProps> = ({
  isOpen,
  onClose,
  offer,
  onSuccess,
}) => {
  const { t } = useTranslations();
  const { refreshPartnerCounters } = useAuth();
  const [campaignType, setCampaignType] = useState<Set<string>>(new Set());
  const [source, setSource] = useState<Set<string>>(new Set());
  const [trialDuration, setTrialDuration] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Check if Free-Trial is selected
  const isFreeTrial = campaignType.has('free_trial');

  // Check if form is valid (campaign type, source are selected, and trial duration for free_trial)
  const isFormValid =
    campaignType.size > 0 && source.size > 0 && (!isFreeTrial || trialDuration.size > 0);

  // Normalize source key for comparison (handle only_fans vs onlyfans variations)
  const normalizeSourceKey = (key: string): string => {
    const normalized = key.toLowerCase().replace(/_/g, '');
    return normalized;
  };

  // Get blocked sources from offer (normalized)
  const blockedSources = useMemo(() => {
    return (offer.offer?.prohibited_sources || []).map((s) => normalizeSourceKey(s));
  }, [offer.offer?.prohibited_sources]);

  // Source options with icons (from proposals constants), filtered to exclude blocked sources
  const sourceOptions = useMemo(() => {
    const allSources = getProposalSources(t);
    return allSources.filter((option) => {
      // Always keep 'all' option, but only if not all sources are blocked
      if (option.key === 'all') {
        // If all non-all sources are blocked, don't show 'all'
        const nonAllSources = allSources.filter((o) => o.key !== 'all');
        const allBlocked = nonAllSources.every((o) =>
          blockedSources.includes(normalizeSourceKey(o.key))
        );
        return !allBlocked;
      }
      // Filter out blocked sources (normalized comparison)
      return !blockedSources.includes(normalizeSourceKey(option.key));
    });
  }, [t, blockedSources]);

  // Reset source selection if currently selected source is blocked
  useEffect(() => {
    const currentSource = Array.from(source)[0];
    if (currentSource && currentSource !== 'all') {
      if (blockedSources.includes(normalizeSourceKey(currentSource))) {
        // Reset to first available source
        const firstAvailable = sourceOptions.find((opt) => opt.key !== 'all');
        if (firstAvailable) {
          setSource(new Set([firstAvailable.key]));
        } else if (sourceOptions.length > 0) {
          setSource(new Set([sourceOptions[0].key]));
        }
      }
    }
  }, [blockedSources, source, sourceOptions]);

  // Trial duration options (показывается только для Free-Trial)
  const trialDurationOptions = [
    { key: '7', label: '7 дней', value: '7' },
    { key: '14', label: '14 дней', value: '14' },
    { key: '30', label: '30 дней', value: '30' },
  ];

  const handleStartCampaign = async () => {
    setIsLoading(true);
    try {
      // Determine campaign type from selection - backend expects 'free' or 'free trial'
      const type = isFreeTrial ? 'free trial' : 'free';

      // Get selected source - backend accepts 'all' as valid source
      const selectedSource = Array.from(source)[0] || 'all';

      // Get trial duration (only for Free-Trial)
      const days = isFreeTrial ? parseInt(Array.from(trialDuration)[0] || '7') : 90;

      await partnerRevshareService.createOrder({
        model_id: offer._id,
        type: type as 'free' | 'free trial',
        source: selectedSource,
        subscribe_days: days,
        expire_days: 0,
      });

      showCreatorSuccess(t('partner.revshare.offers.startCampaign.success'));

      // Update sidebar counters
      await refreshPartnerCounters();

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error creating campaign:', error);
      showError(t('partner.revshare.offers.startCampaign.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      isDismissable={!isLoading}
      classNames={{
        base: 'bg-white',
        header: 'border-b-0',
        body: 'py-3',
        footer: 'border-t border-auth-surface',
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex items-center justify-between px-4 md:px-6 py-6">
              <Typography variant="h4" className="!font-medium">
                {t('partner.revshare.offers.startCampaign.title')} ON{offer.of_user_id}
              </Typography>
            </ModalHeader>

            <ModalBody className="px-4 md:px-6">
              {/* Campaign Type Select */}
              <div className="mb-4">
                <Select
                  label={t('partner.revshare.offers.startCampaign.campaignType')}
                  placeholder={t('partner.revshare.offers.startCampaign.selectCampaignType')}
                  selectedKeys={Array.from(campaignType)}
                  onSelectionChange={(keys) => setCampaignType(new Set(keys as Iterable<string>))}
                  options={[
                    {
                      key: 'link',
                      label: t('partner.revshare.offers.startCampaign.byLink'),
                      value: 'link',
                    },
                    {
                      key: 'free_trial',
                      label: t('partner.revshare.offers.startCampaign.freeTrial'),
                      value: 'free_trial',
                    },
                  ]}
                  fullWidth
                />
              </div>

              {/* Campaign Types Info Block */}
              <div className="mb-4">
                <AlertBlock
                  data={[
                    {
                      severity: 'info',
                      title:
                        t('partner.revshare.offers.startCampaign.byLink') || 'По ссылке кампании',
                      description:
                        t('partner.revshare.offers.startCampaign.byLinkDescription') ||
                        'Все пользователи, которые регистрируются по ссылке кампании, назначаются вам. Другие маркетологи также могут продвигать аккаунт, используя свои соответствующие ссылки',
                    },
                    {
                      severity: 'info',
                      icon: null,
                      title: t('partner.revshare.offers.startCampaign.freeTrial') || 'Free-Trial',
                      description:
                        t('partner.revshare.offers.startCampaign.freeTrialDescription') ||
                        'Все пользователи, зарегистрированные по ссылке бесплатной пробной версии, назначаются вам. Другие маркетологи также могут продвигать аккаунт, используя свои собственные ссылки',
                    },
                  ]}
                />
              </div>

              {/* Trial Duration Select - показывается только для Free-Trial */}
              {isFreeTrial && (
                <div className="mb-4">
                  <Select
                    label={
                      t('partner.revshare.offers.startCampaign.trialDuration') ||
                      'Продолжительность пробного периода'
                    }
                    placeholder={
                      t('partner.revshare.offers.startCampaign.selectDuration') ||
                      'Выберите продолжительность'
                    }
                    selectedKeys={Array.from(trialDuration)}
                    onSelectionChange={(keys) =>
                      setTrialDuration(new Set(keys as Iterable<string>))
                    }
                    options={trialDurationOptions}
                    fullWidth
                  />
                </div>
              )}

              {/* Source Select (Ресурсы/Источники) */}
              <div className="mb-4">
                <Select
                  label={t('partner.revshare.offers.startCampaign.source') || 'Ресурс'}
                  placeholder={
                    t('partner.revshare.offers.startCampaign.selectSource') || 'Выберите ресурс'
                  }
                  selectedKeys={Array.from(source)}
                  onSelectionChange={(keys) => setSource(new Set(keys as Iterable<string>))}
                  options={sourceOptions.map((opt) => ({
                    key: opt.key,
                    label: opt.label,
                    value: opt.key,
                    startContent: opt.icon,
                  }))}
                  fullWidth
                />
              </div>

              {/* Advertising Platforms Info Block */}
              <div className="mb-4">
                <AlertBlock
                  data={[
                    {
                      severity: 'info',
                      title:
                        t('partner.revshare.offers.startCampaign.advertisingPlatforms') ||
                        'Используемые рекламные платформы',
                      description:
                        t(
                          'partner.revshare.offers.startCampaign.advertisingPlatformsDescription'
                        ) ||
                        'Используйте этот параметр, чтобы показать другим маркетологам, какие платформы в настоящее время используются для рекламы. Чтобы продвигать аккаунт на платформе, которая исключена, обратитесь к своему менеджеру для получения доступа',
                    },
                  ]}
                />
              </div>

              {/* Additional Content Info Block */}
              <div className="mb-4">
                <AlertBlock
                  data={[
                    {
                      severity: 'success',
                      icon: (
                        <div style={{ position: 'relative' }}>
                          {' '}
                          <div style={{ position: 'absolute', left: -6, top: -4 }}>
                            <GreenGaleryIc />
                          </div>
                        </div>
                      ),
                      title:
                        t('partner.revshare.offers.startCampaign.additionalContent') ||
                        'Дополнительный контент',
                      description:
                        t('partner.revshare.offers.startCampaign.additionalContentDesc') ||
                        'Если вам нужно больше контента для запуска кампании, обратитесь к вашему менеджеру',
                    },
                  ]}
                  className="!border-l-auth-strength-strong"
                  titleColor="#10793C"
                />
              </div>
            </ModalBody>

            <ModalFooter className="px-4 md:px-6 py-4 flex md:flex-row flex-col justify-between">
              {/* Support Button */}
              <Button
                as="a"
                href="https://t.me/onlyu_partner"
                target="_blank"
                rel="noopener noreferrer"
                variant="bordered"
                startContent={
                  <Icon icon="ic:baseline-telegram" width={20} className="text-partner" />
                }
                className="border-2 border-auth-strength-none rounded-3 text-auth-ink font-semibold"
                isDisabled={isLoading}
              >
                {t('partner.revshare.offers.startCampaign.support')}
              </Button>

              <div className="flex md:flex-row flex-col gap-2">
                {/* Close Button */}
                <Button
                  onPress={handleClose}
                  className="bg-auth-strength-weak/20 text-auth-strength-weak rounded-3 font-normal"
                  isDisabled={isLoading}
                >
                  {t('partner.revshare.offers.startCampaign.close')}
                </Button>

                {/* Start Campaign Button */}
                <Button
                  onPress={handleStartCampaign}
                  className="bg-partner text-white rounded-3 font-semibold min-w-[9.375rem]"
                  isDisabled={isLoading || !isFormValid}
                >
                  {isLoading ? (
                    <Spinner size="sm" color="white" />
                  ) : (
                    t('partner.revshare.offers.startCampaign.start')
                  )}
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </ResponsiveModal>
  );
};

export default StartCampaignModal;
